import React, { useState } from 'react';
import { useParams } from 'react-router';
import { Box, Typography, CircularProgress, Card, CardContent } from '@material-ui/core';
import { ErrorOutline, CheckCircle } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { TabletButton } from '@campfire/tablet-button';
import { PublicScreenWrapper } from '../PublicScreenWrapper';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { GET_PUBLIC_FORM } from './public-form-model.gql';
import { GetPublicForm, GetPublicFormVariables } from './__generated__/GetPublicForm';
import { PublicFormView } from './PublicFormView';

interface OrgLogoHeaderProps {
  orgName?: string;
  orgLogo?: string | null;
  loading: boolean;
  formTitle?: string;
}

const OrgLogoHeader: React.FC<OrgLogoHeaderProps> = ({ orgName, orgLogo, loading, formTitle }) => {
  if (loading) {
    return (
      <Box textAlign='center' marginBottom={4}>
        <Skeleton variant='rect' height={65} width={200} style={{ margin: '0 auto', marginBottom: 16 }} />
        <Skeleton variant='text' height={28} width={300} style={{ margin: '0 auto' }} />
      </Box>
    );
  }

  return (
    <Box marginBottom={3} paddingLeft={1}>
      {orgLogo && (
        <Box marginBottom={3}>
          <img
            src={orgLogo}
            alt={orgName}
            style={{
              height: 65,
              width: 'auto',
              display: 'block',
            }}
          />
        </Box>
      )}
      {formTitle && (
        <Typography
          variant='h5'
          style={{
            color: '#3E3229',
            fontSize: '22px',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            marginBottom: 4,
          }}
        >
          {formTitle}
        </Typography>
      )}
      {orgName && (
        <Typography
          variant='body1'
          style={{
            color: '#646464',
            fontSize: '16px',
            fontWeight: 400,
          }}
        >
          {orgName}
        </Typography>
      )}
    </Box>
  );
};

interface ErrorDisplayProps {
  title: string;
  description: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ title, description }) => {
  return (
    <Box display='flex' alignItems='center' justifyContent='center' minHeight='60vh' marginLeft={2} marginRight={2}>
      <Box textAlign='center'>
        <Box display='flex' justifyContent='center' alignItems='center' marginBottom={2}>
          <ErrorOutline style={{ fontSize: 48, color: '#f44336', marginRight: 8 }} />
        </Box>
        <Typography variant='h5' style={{ fontWeight: 'bold' }} gutterBottom>
          {title}
        </Typography>
        <Typography variant='body1' color='textSecondary' style={{ marginBottom: 24 }}>
          {description}
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          If you continue to have problems, please contact the organisation.
        </Typography>
      </Box>
    </Box>
  );
};

interface SuccessDisplayProps {
  orgName?: string;
  orgLogo?: string | null;
  formTitle?: string;
  successMessage?: string | null;
  onSubmitAnother?: () => void;
}

const SuccessDisplay: React.FC<SuccessDisplayProps> = ({
  orgName,
  orgLogo,
  formTitle,
  successMessage,
  onSubmitAnother,
}) => {
  return (
    <Box>
      <OrgLogoHeader orgName={orgName} orgLogo={orgLogo} loading={false} formTitle={formTitle} />
      <Box display='flex' alignItems='center' justifyContent='center' minHeight='40vh'>
        <Card style={{ textAlign: 'center', padding: '24px', backgroundColor: 'transparent', boxShadow: 'none' }}>
          <CardContent>
            <CheckCircle style={{ fontSize: '40px', color: '#4caf50', marginBottom: '12px' }} />

            <Typography variant='h5' gutterBottom style={{ fontWeight: 600, color: '#3E3229', marginBottom: '8px' }}>
              Submitted
            </Typography>

            <Typography variant='body1' gutterBottom style={{ color: '#3E3229', marginBottom: '16px' }}>
              {successMessage || 'Your submission has been received.'}
            </Typography>

            <Typography variant='body2' color='textSecondary'>
              You may now close this window.
            </Typography>

            {onSubmitAnother && (
              <Box paddingTop={3}>
                <TabletButton
                  size='small'
                  variant='text'
                  color='primary'
                  onClick={onSubmitAnother}
                  style={{ fontSize: '13px', textTransform: 'none' }}
                >
                  Submit another
                </TabletButton>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export function PublicFormScreen() {
  const { formId } = useParams<{ formId: string }>();
  const [submitted, setSubmitted] = useState(false);
  const { isMobile } = useCampfireTheme();

  const { data, loading, error } = useCampfireQuery<GetPublicForm, GetPublicFormVariables>(GET_PUBLIC_FORM, {
    options: {
      variables: { formId: formId || '' },
    },
  });

  if (loading) {
    return (
      <PublicScreenWrapper hideHeader>
        <Box marginTop={isMobile ? 3 : 7} marginBottom={8} marginLeft={2} marginRight={2}>
          <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
            <CircularProgress />
          </Box>
        </Box>
      </PublicScreenWrapper>
    );
  }

  if (error || !data?.vm.form) {
    return (
      <PublicScreenWrapper hideHeader>
        <ErrorDisplay
          title='Form Not Found'
          description='This form could not be found or is no longer available. Please check the link you received.'
        />
      </PublicScreenWrapper>
    );
  }

  const { form } = data.vm;

  if (!form.isPubliclyAccessible) {
    return (
      <PublicScreenWrapper hideHeader>
        <ErrorDisplay
          title='Form Not Available'
          description='This form is not currently accepting submissions. It may be closed or unlisted.'
        />
      </PublicScreenWrapper>
    );
  }

  if (form.dateClosed) {
    const closedDate = new Date(form.dateClosed);
    const now = new Date();
    if (closedDate <= now) {
      return (
        <PublicScreenWrapper hideHeader>
          <ErrorDisplay
            title='Form Closed'
            description={
              form.closedMessage || 'This form is no longer accepting submissions. The submission period has ended.'
            }
          />
        </PublicScreenWrapper>
      );
    }
  }

  const alreadySubmitted = !form.allowMultipleSubmissions && form.hasCurrentUserSubmitted;

  if (submitted || alreadySubmitted) {
    const canSubmitAnother = submitted && !alreadySubmitted && form.allowMultipleSubmissions;
    return (
      <PublicScreenWrapper hideHeader shrink={isMobile ? true : 1000}>
        <Box paddingX={isMobile ? 2 : 4} paddingTop={isMobile ? 3 : 5} paddingBottom={isMobile ? 2 : 3}>
          <SuccessDisplay
            orgName={data.orgName}
            orgLogo={data.orgLogo}
            formTitle={form.title}
            successMessage={form.successMessage}
            onSubmitAnother={canSubmitAnother ? () => setSubmitted(false) : undefined}
          />
        </Box>
      </PublicScreenWrapper>
    );
  }

  return (
    <PublicScreenWrapper hideHeader shrink={isMobile ? true : 1000}>
      <Box paddingX={isMobile ? 2 : 4} paddingTop={isMobile ? 3 : 5} paddingBottom={isMobile ? 2 : 3}>
        <OrgLogoHeader orgName={data.orgName} orgLogo={data.orgLogo} loading={false} formTitle={form.title} />
        <PublicFormView form={form} onSubmitSuccess={() => setSubmitted(true)} />
      </Box>
    </PublicScreenWrapper>
  );
}
